<script setup lang="ts">
import { EmptyState, TraceIdAlert, formatDateTime } from '@yc/ui-kit'
import { computed, onMounted, reactive, ref } from 'vue'
import { useRouter } from 'vue-router'

import { MOVE_LABELS, listStocks, transferStock, type StockRow } from '../api/stock'
import { listLocations, type Location } from '../api/locations'
import { listWarehouses, type Warehouse } from '../api/warehouses'

const router = useRouter()

const rows = ref<StockRow[]>([])
const total = ref(0)
const loading = ref(false)
const error = ref<unknown>(null)
const notice = ref('')

const warehouses = ref<Warehouse[]>([])
const locations = ref<Location[]>([])
const refError = ref<unknown>(null)

// 参照下拉用 undefined 表示「未选」：0 会被 el-select 当成值显示成「0」，placeholder 就没了
const query = reactive({
  keyword: '',
  warehouse: undefined as number | undefined,
  location: undefined as number | undefined,
  page: 1,
  size: 20,
})

const transferOpen = ref(false)
const transferError = ref<unknown>(null)
const saving = ref(false)
const target = ref<StockRow | null>(null)
const form = reactive({ to_location_id: undefined as number | undefined, qty: 1 })

const targetLocations = computed(() =>
  locations.value.filter((l) => l.id !== target.value?.location_id),
)

const qtyError = computed(() => {
  if (!target.value) return ''
  if (!(form.qty > 0)) return '移动数量必须大于 0'
  if (form.qty > target.value.available) return `可用库存只有 ${target.value.available}`
  return ''
})

async function load(noticeText = ''): Promise<void> {
  loading.value = true
  error.value = null
  try {
    const res = await listStocks({
      page: query.page,
      size: query.size,
      keyword: query.keyword.trim() || undefined,
      warehouse: query.warehouse || undefined,
      location: query.location || undefined,
    })
    rows.value = res.list
    total.value = res.total
    notice.value = noticeText
  } catch (e) {
    error.value = e
    rows.value = []
  } finally {
    loading.value = false
  }
}

async function loadRefs(): Promise<void> {
  refError.value = null
  try {
    warehouses.value = (await listWarehouses({ page: 1, size: 100 })).list
  } catch (e) {
    // 参照数据拿不到下拉会空，但必须把原因露出来（如未选货主时后端回 422），否则页面看着像没数据
    refError.value = e
  }
  await loadLocations()
}

async function loadLocations(): Promise<void> {
  try {
    locations.value = (
      await listLocations({ page: 1, size: 200, warehouse: query.warehouse || undefined })
    ).list
  } catch (e) {
    locations.value = []
    if (!refError.value) refError.value = e
  }
}

onMounted(() => {
  void load()
  void loadRefs()
})

function search(): void {
  query.page = 1
  void load()
}

function page(p: number): void {
  query.page = p
  void load()
}

async function onWarehouseChange(): Promise<void> {
  query.location = undefined
  await loadLocations()
  search()
}

function openTransfer(row: StockRow): void {
  target.value = row
  form.qty = row.available
  form.to_location_id = targetLocations.value[0]?.id
  transferError.value = null
  transferOpen.value = true
}

async function submitTransfer(): Promise<void> {
  if (!target.value || !form.to_location_id || qtyError.value) return
  saving.value = true
  transferError.value = null
  try {
    await transferStock({
      stock_id: target.value.id,
      to_location_id: form.to_location_id,
      qty: Number(form.qty),
    })
    transferOpen.value = false
    await load('移库完成')
  } catch (e) {
    transferError.value = e
  } finally {
    saving.value = false
  }
}

function moves(): void {
  void router.push({ name: 'stock-moves' })
}
</script>

<template>
  <div class="yc-stk">
    <header class="yc-stk__bar">
      <el-input
        v-model="query.keyword"
        class="yc-stk__keyword"
        placeholder="商品编码/名称/条码或批次"
        clearable
        @keyup.enter="search"
        @clear="search"
      />
      <el-select
        v-model="query.warehouse"
        class="yc-stk__ref"
        placeholder="全部仓库"
        clearable
        @change="onWarehouseChange"
      >
        <el-option
          v-for="w in warehouses"
          :key="w.id"
          :label="`${w.code} ${w.name}`.trim()"
          :value="w.id"
        />
      </el-select>
      <el-select v-model="query.location" class="yc-stk__ref" placeholder="全部库位" clearable @change="search">
        <el-option v-for="l in locations" :key="l.id" :label="l.code" :value="l.id" />
      </el-select>
      <el-button @click="search">查询</el-button>
      <el-button @click="moves">库存流水</el-button>
      <el-button @click="load()">刷新</el-button>
      <span v-if="notice" class="yc-stk__notice">{{ notice }}</span>
    </header>

    <div v-if="error || refError" class="yc-stk__error">
      <TraceIdAlert :error="error ?? refError" />
    </div>

    <el-card v-loading="loading" class="yc-stk__card">
      <el-table v-if="rows.length" :data="rows" row-key="id">
        <el-table-column label="仓库" min-width="130">
          <template #default="{ row }">{{ row.warehouse_name || `#${row.warehouse_id}` }}</template>
        </el-table-column>
        <el-table-column prop="location_code" label="库位" width="120" />
        <el-table-column prop="sku_code" label="商品编码" width="130" />
        <el-table-column prop="sku_name" label="商品名称" min-width="150" show-overflow-tooltip />
        <el-table-column prop="unit" label="单位" width="70" />
        <el-table-column label="批次" width="120">
          <template #default="{ row }">{{ row.batch_no || '—' }}</template>
        </el-table-column>
        <el-table-column label="效期" width="110">
          <template #default="{ row }">
            {{ row.expiry_date ? formatDateTime(row.expiry_date).slice(0, 10) : '—' }}
          </template>
        </el-table-column>
        <el-table-column prop="qty" label="库存" width="80" />
        <el-table-column prop="locked_qty" label="锁定" width="80" />
        <el-table-column label="可用" width="80">
          <template #default="{ row }">
            <span :class="{ 'yc-stk__zero': row.available <= 0 }">{{ row.available }}</span>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="100" fixed="right">
          <template #default="{ row }">
            <el-button
              v-perm="'wms:stock:transfer'"
              :disabled="row.available <= 0"
              link
              type="primary"
              @click="openTransfer(row)"
            >
              移库
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <EmptyState
        v-else-if="!loading"
        title="还没有库存"
        description="收货入库后库存会出现在这里；也可以去出库单走一遍闭环。"
      />
    </el-card>

    <el-pagination
      v-if="total > query.size"
      class="yc-stk__page"
      layout="total, prev, pager, next"
      :total="total"
      :page-size="query.size"
      :current-page="query.page"
      @current-change="page"
    />

    <el-dialog
      v-if="transferOpen"
      v-model="transferOpen"
      :title="`移库 · ${target?.sku_code ?? ''}`"
      width="520px"
      :close-on-click-modal="false"
    >
      <div v-if="transferError" class="yc-stk__error">
        <TraceIdAlert :error="transferError" />
      </div>

      <el-form label-position="top" @submit.prevent="submitTransfer">
        <el-descriptions :column="1" border>
          <el-descriptions-item label="商品">
            {{ target?.sku_code }} {{ target?.sku_name }}
          </el-descriptions-item>
          <el-descriptions-item label="批次">{{ target?.batch_no || '—' }}</el-descriptions-item>
          <el-descriptions-item label="当前库位">{{ target?.location_code }}</el-descriptions-item>
          <el-descriptions-item label="可用">{{ target?.available }} {{ target?.unit }}</el-descriptions-item>
        </el-descriptions>

        <el-form-item class="yc-stk__target" label="目标库位">
          <el-select v-model="form.to_location_id" filterable placeholder="选择库位">
            <el-option v-for="l in targetLocations" :key="l.id" :label="l.code" :value="l.id" />
          </el-select>
        </el-form-item>

        <el-form-item label="移动数量" :error="qtyError">
          <el-input-number v-model="form.qty" :min="1" :max="target?.available ?? 1" controls-position="right" />
        </el-form-item>

        <el-button
          type="primary"
          native-type="submit"
          :loading="saving"
          :disabled="!!qtyError || !form.to_location_id"
        >
          确认移库
        </el-button>
        <span class="yc-stk__hint">流水类型记为「{{ MOVE_LABELS.transfer }}」，操作人记当前登录人</span>
      </el-form>

      <template #footer>
        <el-button @click="transferOpen = false">取消</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<style scoped lang="scss">
.yc-stk {
  &__bar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--yc-space-2);
    margin-bottom: var(--yc-space-4);
  }

  &__keyword {
    width: 230px;
  }

  &__ref {
    width: 180px;
  }

  &__notice {
    color: var(--yc-text-secondary);
    font-size: var(--yc-font-sm);
  }

  &__error {
    margin-bottom: var(--yc-space-3);
    font-size: var(--yc-font-sm);
  }

  &__zero {
    color: var(--el-color-warning);
  }

  &__page {
    margin-top: var(--yc-space-4);
    justify-content: flex-end;
  }

  &__target {
    margin-top: var(--yc-space-3);
  }

  &__hint {
    margin-left: var(--yc-space-3);
    color: var(--yc-text-secondary);
    font-size: var(--yc-font-xs);
  }
}
</style>
